import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { CopyButton } from '~/eds/src/components/Button/subcomponents/CopyButton';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { EntityType } from '~/enums/EntityType';
import { AlgorithmMetric } from '~/features/process-documents/types';
import { ModelScopeSelect } from '~/features/x-ray/ModelDetails/Configuration/ModelScopeSelect';
import { ModelResults } from '~/features/x-ray/ModelResults';
import { PromptModel } from '~/features/x-ray/types/deprecated';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { generateNav } from '~/routing/nav';
import { useRouting } from '~/routing/useRouting';

const TABLE_OPTIONS = {
  enableExportXlsx: false,
  enableManageColumns: false,
  enableSelectRows: true,
};

const PAGE_INDEX = 1;

const Page = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { params, getSearchParams, setSearchParams } = useRouting();
  const clientId = Number(params.clientId);
  const { toast } = useToast();
  const searchParam = getSearchParams();
  const selectedTab = searchParam.tab || 'custom-ai';

  const query = useSelector(slices.xRay.selectors.selectQuery);

  const [modelId, setModelId] = useState<string | null>(null);

  const [failedDocIds, setFailedDocIds] = useState<string[]>([]);
  const [publishMode, setPublishMode] = useState<string | null>(
    'all_docs_in_scope',
  );

  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const enableSuperAdminRepublishing = useFlag(
    FlagType.CustomAiSuperAdminRepublishing,
  );

  const {
    data: algorithmMetrics = [],
    isFetching: isLoadingAlgorithms,
    error,
  } = api.endpoints.getAlgorithmMetrics.useQuery(
    { clientId },
    { skip: !clientId },
  );

  const [
    getModel,
    { data: model, isLoading: isLoadingModel, isError: isModelError },
  ] = api.endpoints.getPromptModel.useLazyQuery();

  const latestVersion =
    model && model.versions.length > 0 ? Math.max(...model.versions) : 1;

  const [
    fetchModelVersion,
    {
      currentData: modelVersion,
      isFetching: isLoadingModelVersion,
      isError: isModelVersionError,
    },
  ] = api.endpoints.getPromptModelVersion.useLazyQuery();

  const [republishModel, { isLoading: isRepublishingModel }] =
    api.endpoints.adminRepublishModel.useMutation();

  useEffect(() => {
    if (modelVersion) {
      dispatch(slices.xRay.actions.setQuery(modelVersion.scope));
    }
  }, [modelVersion]);

  const isLoadingScope =
    isLoadingModelVersion || (isLoadingModel && !modelVersion);

  const [reprocessAlgorithm] = api.endpoints.reprocessAlgorithms.useMutation();
  const [stopReprocessAlgorithm] =
    api.endpoints.stopReprocessAlgorithms.useMutation();
  const [getFailedDocs, { isLoading: isQueryingFailedDocs }] =
    api.endpoints.getModelFailedDocs.useLazyQuery();

  const nav = generateNav({
    current: {
      text: intl.formatMessage({
        id: 'ProcessDocuments.navTitle',
        defaultMessage: 'Document Processing',
        description: 'Navigation title for document processing page',
      }),
      pathname: RoutePathType.AdminConsoleClientProcessDocuments,
    },
    from: RoutePathType.AdminConsoleClient,
    params: {
      clientId,
    },
    intl,
  });

  const placeholderContent =
    algorithmMetrics.length === 0
      ? {
          message: intl.formatMessage({
            id: 'ProcessDocuments.noAlgorithmsMessage',
            defaultMessage: 'No algorithms found.',
            description: 'Message when no algorithms are found',
          }),
        }
      : error
        ? {
            message: intl.formatMessage({
              id: 'ProcessDocuments.errorLoadingMessage',
              defaultMessage: 'Error loading upload logs.',
              description: 'Error message when loading upload logs fails',
            }),
          }
        : undefined;

  useEffect(() => {
    if (!searchParam?.tab) {
      setSearchParams({ tab: 'custom-ai' });
    }
  }, [searchParam?.tab, setSearchParams]);

  const columns = [
    {
      key: 'name',
      cellType: 'text',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.algorithmColumn',
        defaultMessage: 'Algorithm',
        description: 'Algorithm column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => ({
        text: d.title,
      }),
    },
    {
      key: 'failed',
      cellType: 'number',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.failedColumn',
        defaultMessage: 'Failed',
        description: 'Failed column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => ({
        number: d.counts.failed,
      }),
    },
    {
      key: 'unprocessed',
      cellType: 'number',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.unprocessedColumn',
        defaultMessage: 'Unprocessed',
        description: 'Unprocessed column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => ({
        number: d.counts.notRun,
      }),
    },
    {
      key: 'successful',
      cellType: 'number',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.successfulColumn',
        defaultMessage: 'Successful',
        description: 'Successful column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => ({
        number: d.counts.successful,
      }),
    },
    {
      key: 'lastRun',
      cellType: 'datetime',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.lastRanColumn',
        defaultMessage: 'Last Ran',
        description: 'Last ran column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => ({
        datetime: d.lastRunDate,
        format: 'short_datetime',
      }),
      width: 'auto',
    },
    {
      key: 'active',
      cellType: 'chips',
      disableSortBy: true,
      title: intl.formatMessage({
        id: 'ProcessDocuments.activeColumn',
        defaultMessage: 'Active',
        description: 'Active column header',
      }),
      mapCellProps: (d: AlgorithmMetric) => {
        if (d.title === 'Total') return { chips: [] };
        return {
          chips: [
            {
              text:
                d.status === 'active'
                  ? intl.formatMessage({
                      id: 'ProcessDocuments.activeStatus',
                      defaultMessage: 'Active',
                      description: 'Status label for active algorithms',
                    })
                  : intl.formatMessage({
                      id: 'ProcessDocuments.inactiveStatus',
                      defaultMessage: 'Inactive',
                      description: 'Status label for inactive algorithms',
                    }),
              status: d.status,
            },
          ],
        };
      },
    },
  ];

  const getBulkActions = (state: any) => {
    const algorithms = state.selectedRowIds.filter(
      (id: string) => id !== 'Total',
    );

    const bulkActionsConfigs = [
      {
        label: intl.formatMessage({
          id: 'ProcessDocuments.runFailedAction',
          defaultMessage: 'Run Failed',
          description: 'Run failed algorithms action label',
        }),
        condition: 'failed',
        tooltip: intl.formatMessage({
          id: 'ProcessDocuments.runFailedTooltip',
          defaultMessage: 'Run failed algorithms',
          description: 'Tooltip for run failed algorithms action',
        }),
      },
      {
        label: intl.formatMessage({
          id: 'ProcessDocuments.runUnprocessedAction',
          defaultMessage: 'Run Unprocessed',
          description: 'Run unprocessed algorithms action label',
        }),
        condition: 'unprocessed',
        tooltip: intl.formatMessage({
          id: 'ProcessDocuments.runUnprocessedTooltip',
          defaultMessage: 'Run unprocessed algorithms',
          description: 'Tooltip for run unprocessed algorithms action',
        }),
      },
      {
        label: intl.formatMessage({
          id: 'ProcessDocuments.runAllAction',
          defaultMessage: 'Run All',
          description: 'Run all algorithms action label',
        }),
        condition: 'all',
        tooltip: intl.formatMessage({
          id: 'ProcessDocuments.runAllTooltip',
          defaultMessage: 'Run all algorithms',
          description: 'Tooltip for run all algorithms action',
        }),
      },
      {
        label: intl.formatMessage({
          id: 'ProcessDocuments.stopTaskAction',
          defaultMessage: 'Stop Task',
          description: 'Stop task action label',
        }),
        condition: 'stop',
        tooltip: intl.formatMessage({
          id: 'ProcessDocuments.stopTaskTooltip',
          defaultMessage: 'Stop reprocessing algorithms',
          description: 'Tooltip for stop reprocessing algorithms action',
        }),
      },
    ];

    return bulkActionsConfigs.map((bulkAction) => {
      const condition = bulkAction.condition;
      return {
        label: bulkAction.label,
        onClick: () => {
          switch (condition) {
            case 'stop':
              stopReprocessAlgorithm({
                clientId,
                algorithms,
              })
                .unwrap()
                .then(() => {
                  toast({
                    message: intl.formatMessage(
                      {
                        id: 'ProcessDocuments.stoppedProcessingSuccess',
                        defaultMessage: 'Stopped processing {algorithms}',
                        description:
                          'Success message when processing is stopped. Example: algorithms = "Algorithm1, Algorithm2"',
                      },
                      {
                        algorithms: algorithms.join(', '),
                      },
                    ),
                    status: 'success',
                  });
                })
                .catch(() => {
                  toast({
                    message: intl.formatMessage(
                      {
                        id: 'ProcessDocuments.stopProcessingError',
                        defaultMessage:
                          'An error occurred while trying to stop reprocessing {condition} documents',
                        description:
                          'Error message when stopping reprocessing fails. Example: condition = "failed", "unprocessed"',
                      },
                      {
                        condition,
                      },
                    ),
                    status: 'danger',
                  });
                });
              break;
            case 'all':
            case 'failed':
            case 'unprocessed':
              reprocessAlgorithm({
                clientId,
                algorithms,
                condition,
              })
                .unwrap()
                .then(() => {
                  toast({
                    message: intl.formatMessage(
                      {
                        id: 'ProcessDocuments.rerunningSuccess',
                        defaultMessage: 'Rerunning {condition} documents',
                        description:
                          'Success message when rerunning documents. Example: condition = "failed", "unprocessed", "all"',
                      },
                      {
                        condition,
                      },
                    ),
                    status: 'success',
                  });
                })
                .catch(() => {
                  toast({
                    message: intl.formatMessage(
                      {
                        id: 'ProcessDocuments.reprocessError',
                        defaultMessage:
                          'An error occurred while trying to reprocess {condition} documents',
                        description:
                          'Error message when reprocessing fails. Example: condition = "failed", "unprocessed", "all"',
                      },
                      {
                        condition,
                      },
                    ),
                    status: 'danger',
                  });
                });
              break;
          }
        },
        tooltip: bulkAction.tooltip,
      };
    });
  };

  const [
    newReprocessingRequestModal,
    showNewReprocessingRequestModal,
    hideNewReprocessingRequestModal,
  ] = useModal({
    isFullPage: true,
    isVisible: true,
    title: intl.formatMessage({
      id: 'ProcessDocuments.newReprocessingRequestModalTitle',
      defaultMessage: 'New Reprocessing Request',
      description: 'Title for new reprocessing request modal',
    }),
    primaryAction: {
      disabled:
        !modelVersion ||
        !modelId ||
        isRepublishingModel ||
        (failedDocIds.length === 0 && publishMode === 'failed_docs_in_scope'),
      tooltip: isRepublishingModel
        ? 'Republishing model... Please wait.'
        : failedDocIds.length === 0 && publishMode === 'failed_docs_in_scope'
          ? 'No failed documents to republish, please select a different publish mode'
          : !modelVersion || !modelId
            ? 'Please enter a model ID and fetch the model version'
            : undefined,
      text: intl.formatMessage({
        id: 'ProcessDocuments.republishModelAction',
        defaultMessage: 'Republish Model',
        description: 'Action label for republish model',
      }),
      onClick: () => {
        republishModel({
          modelId: modelId!,
          version: latestVersion,
          publishMode: publishMode!,
          overrideScope: query,
        })
          .then(() => {
            toast({
              message: intl.formatMessage({
                id: 'ProcessDocuments.republishModelSuccess',
                defaultMessage: 'Model republished successfully',
                description:
                  'Success message when model republished successfully',
              }),
            });
          })
          .catch(() => {
            toast({
              message: intl.formatMessage({
                id: 'ProcessDocuments.republishModelError',
                defaultMessage: 'Error republishing model',
                description: 'Error message when republishing model fails',
              }),
            });
          });
      },
    },
    children: (
      <Layout preset="sections">
        <FormField<string, false>
          input={TextInput}
          inputProps={{
            placeholder: 'Enter Model ID',
          }}
          label="Model ID"
          name="model-id"
          value={modelId}
          onChange={(value) => {
            setModelId(value);
            setFailedDocIds([]);
            setPublishMode('all_docs_in_scope');
            dispatch(slices.xRay.actions.setQuery([]));
          }}
          info={intl.formatMessage({
            id: 'ProcessDocuments.provideModelInformationInfo',
            defaultMessage:
              'Enter the model ID you want to use for the reprocessing request.',
            description: 'Info for provide model information',
          })}
          error={
            isModelError || isModelVersionError
              ? 'There was an error loading model'
              : undefined
          }
        />
        <Box sx={{ justifyContent: 'flex-start' }}>
          <Button
            text="Get Model"
            isLoading={isLoadingModelVersion || isLoadingModel}
            onClick={() => {
              getModel({ modelId: modelId! }).then(
                (response: { data?: PromptModel }) => {
                  const modelData = response.data;
                  const calculatedLatestVersion =
                    modelData && modelData.versions.length > 0
                      ? Math.max(...modelData.versions)
                      : 1;
                  fetchModelVersion({
                    modelId: modelId!,
                    version: calculatedLatestVersion,
                    highlightMethod,
                  });
                },
              );
            }}
            level="primary"
            disabled={!modelId}
            tooltip={
              !modelId
                ? 'Please enter a model ID'
                : isLoadingModelVersion || isLoadingModel
                  ? 'Fetching model version... Please wait.'
                  : undefined
            }
          />
        </Box>

        {modelVersion && (
          <>
            <FormField<string, false>
              input={Select}
              inputProps={{
                placeholder: 'Select Publish Mode',
                options: [
                  {
                    label: 'Reprocess All Documents in Scope',
                    value: 'all_docs_in_scope',
                  },
                  {
                    label: 'Reprocess New Documents in Scope',
                    value: 'new_docs_in_scope',
                  },
                  {
                    label: 'Reprocess Failed Documents',
                    value: 'failed_docs_in_scope',
                  },
                ],
              }}
              label="Publish Mode"
              name="publish-mode"
              value={publishMode}
              onChange={(value) => {
                setPublishMode(value);
              }}
              info={intl.formatMessage({
                id: 'ProcessDocuments.publishModeInfo',
                defaultMessage:
                  'This will determine which documents will be reprocessed with the override scope.',
                description: 'Info for publish mode',
              })}
            />
            {publishMode === 'failed_docs_in_scope' && (
              <>
                <Box sx={{ justifyContent: 'flex-start' }}>
                  <Button
                    text="Fetch Failed Docs"
                    onClick={() => {
                      getFailedDocs({ modelId: modelId! })
                        .then((response) => {
                          setFailedDocIds(response?.data?.doc_ids || []);
                        })
                        .catch(() => {
                          toast({
                            message: intl.formatMessage({
                              id: 'ProcessDocuments.failedDocsError',
                              defaultMessage: 'Error fetching failed docs',
                              description:
                                'Error message when fetching failed docs fails',
                            }),
                          });
                        });
                    }}
                    level="primary"
                    disabled={isQueryingFailedDocs}
                    tooltip={
                      isQueryingFailedDocs
                        ? intl.formatMessage({
                            id: 'ProcessDocuments.fetchingFailedDocsTooltip',
                            defaultMessage:
                              "fetching all doc_ids that failed for this model's latest publish",
                            description:
                              'Tooltip for fetching failed docs button',
                          })
                        : undefined
                    }
                  />
                </Box>
                {failedDocIds.length > 0 && (
                  <>
                    <FormField
                      readOnly={true}
                      input={TextArea}
                      inputProps={{
                        maxH: 500,
                      }}
                      onChange={noop}
                      label={intl.formatMessage({
                        id: 'ProcessDocuments.failedDocumentIdsLabel',
                        defaultMessage: 'Failed Document IDs',
                        description: 'Label for failed document IDs',
                      })}
                      name="failed-doc-ids"
                      value={failedDocIds.join(', \n')}
                    />
                    <CopyButton
                      copyText={failedDocIds.join(', \n')}
                      disabled={failedDocIds.length === 0}
                    />
                  </>
                )}
              </>
            )}
            <ContentContainer loadingContent={{ isLoading: isLoadingScope }}>
              <ModelScopeSelect
                readOnly={!modelVersion}
                info={intl.formatMessage({
                  id: 'ProcessDocuments.overrideScopeInfo',
                  defaultMessage:
                    'This scope will replace the current scope of the model when reprocessing request.',
                  description: 'Info for override scope',
                })}
                entityId={null}
                entityType={EntityType.DocumentAttachedData}
                title="Override Scope"
              />
            </ContentContainer>
          </>
        )}
      </Layout>
    ),
    onCancel: () => {
      setModelId(null);
      setPublishMode(null);
      setFailedDocIds([]);
      dispatch(slices.xRay.actions.setQuery([]));

      hideNewReprocessingRequestModal();
    },
  });

  const children = (
    <ContentContainer
      loadingContent={{ isLoading: isLoadingAlgorithms }}
      placeholderContent={placeholderContent}
    >
      <Table
        name={intl.formatMessage({
          id: 'ProcessDocuments.algorithmsLogsTableName',
          defaultMessage: 'Algorithms Logs',
          description: 'Table name for algorithms logs',
        })}
        data={algorithmMetrics}
        getBulkActions={getBulkActions}
        columns={columns}
        options={TABLE_OPTIONS}
        state={{
          pageIndex: PAGE_INDEX,
          pageSize: algorithmMetrics.length,
        }}
      />
    </ContentContainer>
  );

  return (
    <>
      <PageLayout
        title={intl.formatMessage({
          id: 'ProcessDocuments.pageTitle',
          defaultMessage: 'Algorithm Metrics',
          description: 'Page title for algorithm metrics page',
        })}
        nav={nav}
        children={enableSuperAdminRepublishing ? undefined : children}
        placeholderContent={
          enableSuperAdminRepublishing ? undefined : placeholderContent
        }
        loadingContent={
          enableSuperAdminRepublishing
            ? undefined
            : { isLoading: isLoadingAlgorithms }
        }
        actions={
          selectedTab === 'custom-ai' && enableSuperAdminRepublishing
            ? [
                {
                  text: intl.formatMessage({
                    id: 'ProcessDocuments.newReprocessingRequestAction',
                    defaultMessage: 'New Reprocessing Request',
                    description: 'Action label for new reprocessing request',
                  }),
                  onClick: showNewReprocessingRequestModal,
                  level: 'primary',
                  icon: 'plus',
                  iconPosition: 'left',
                },
              ]
            : []
        }
        tabs={
          enableSuperAdminRepublishing
            ? {
                selectedTab,
                onSelectTab: (tab: string) => {
                  setSearchParams({
                    tab: tab,
                  });
                },
                tabs: [
                  {
                    label: 'Custom AI',
                    value: 'custom-ai',
                    panel: (
                      <ModelResults
                        tab="republished"
                        entityType={EntityType.Field}
                        onShowResult={(modelResult) =>
                          dispatch(
                            slices.xRay.actions.setActiveModelResultInfo({
                              id: modelResult.id,
                              latestVersion: modelResult.latestVersion,
                              libraryModelId: modelResult.libraryModelId,
                              targetEntity: modelResult.targetEntity,
                              targetEntityDetails:
                                modelResult.targetEntityDetails,
                            }),
                          )
                        }
                      />
                    ),
                  },
                  {
                    label: 'OCR + Standard AI',
                    value: 'ocr-standard-ai',
                    panel: children,
                  },
                ],
              }
            : undefined
        }
      />
      {newReprocessingRequestModal}
    </>
  );
};

export default Page;
